import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { DeleteAccountDialog } from '@/components/DeleteAccountDialog';
import { ReasonDialog } from '@/components/ReasonDialog';
import { StatusBanner } from '@/components/StatusBanner';
import { useMe } from '@/lib/useMe';
import { canDeleteAccount, roleAtLeast } from '@/lib/roles';
import {
  AdminApiError,
  cancelSubscription,
  deletePaymentToken,
  fetchPayments,
  refundOrder,
} from '@/lib/paymentsApi';

type DialogKind = 'cancel' | 'delete-token' | 'refund' | null;

/**
 * `/users/:id/payments` (ClickUp 869f0ev24 §4 / 869f0ev30) — the support
 * console's own home for money actions and account deletion. No sibling
 * `/users/:id` card exists on `dev` yet (Users+Content Phase 1 build), so
 * this is its own route rather than a tab folded into a page that is not
 * there — the brief names this fallback explicitly.
 *
 * Buttons are ROLE-GATED by hiding, not just disabling: a support agent never
 * sees a Refund button at all (finance+), and nobody but superadmin sees the
 * danger section — the API's own role check is still what actually enforces
 * this (roles.ts's own docstring), this is the UI half of least-privilege.
 */
export function UserPaymentsPage() {
  const { id: profileId } = useParams<{ id: string }>();
  const { data: me } = useMe();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [refundOrderId, setRefundOrderId] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<{ kind: 'error' | 'success'; message: string } | null>(
    null,
  );

  const paymentsQuery = useQuery({
    queryKey: ['admin-payments', profileId],
    queryFn: () => fetchPayments(profileId as string),
    enabled: Boolean(profileId),
    retry: false,
  });

  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!profileId) return null;

  const canSupport = roleAtLeast(me?.role, 'support');
  const canFinance = roleAtLeast(me?.role, 'finance');
  // The Delete account section: `support` + `superadmin` since the owner's
  // decision of 21.09.2026 (ClickUp 869f4a4a0) — one named helper, never a
  // rank, so this cannot drift from ACCOUNT_DELETION_ROLES on the API side.
  const canDelete = canDeleteAccount(me?.role);

  async function runAction(reason: string) {
    if (dialog === 'refund' && !refundOrderId.trim()) {
      setBanner({ kind: 'error', message: "'Order id' is required." });
      return;
    }
    setBusy(true);
    setBanner(null);
    try {
      if (dialog === 'delete-token') {
        // The receipt says what payment-back actually did (ClickUp 869f353ff).
        // The old fixed string read as a deletion even when nothing was
        // deleted — and for months nothing was.
        const { deleted } = await deletePaymentToken(profileId as string, reason);
        setBanner(
          deleted.length
            ? {
                kind: 'success',
                message: `Deleted ${deleted.length} payment token${deleted.length === 1 ? '' : 's'}.`,
              }
            : { kind: 'success', message: 'No stored payment token to delete.' },
        );
      } else if (dialog === 'cancel') {
        const { cancelled } = await cancelSubscription(profileId as string, reason);
        setBanner(
          cancelled.length
            ? {
                kind: 'success',
                message: `Cancelled ${cancelled.length} subscription${cancelled.length === 1 ? '' : 's'}.`,
              }
            : { kind: 'success', message: 'No active subscription to cancel.' },
        );
      } else if (dialog === 'refund') {
        await refundOrder(profileId as string, refundOrderId.trim(), reason);
        setBanner({ kind: 'success', message: `Order ${refundOrderId.trim()} refunded.` });
      }
      setDialog(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-payments', profileId] });
    } catch (err) {
      setBanner({
        kind: 'error',
        message: err instanceof AdminApiError ? err.message : 'The request failed.',
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <h1 className="text-h2 font-semibold text-fg-primary">Payments</h1>
      <p className="mt-2 text-body text-fg-secondary">Profile {profileId}</p>

      {banner ? (
        <div className="mt-4">
          <StatusBanner kind={banner.kind} message={banner.message} />
        </div>
      ) : null}

      <div className="mt-6 flex flex-wrap gap-2">
        {canSupport ? (
          <button
            type="button"
            className="rounded-card border border-border px-3 py-2 text-body text-fg-primary hover:bg-state-hover"
            onClick={() => setDialog('delete-token')}
          >
            Delete payment token
          </button>
        ) : null}
        {canSupport ? (
          <button
            type="button"
            className="rounded-card border border-border px-3 py-2 text-body text-fg-primary hover:bg-state-hover"
            onClick={() => setDialog('cancel')}
          >
            Cancel subscription
          </button>
        ) : null}
        {canFinance ? (
          <button
            type="button"
            className="rounded-card border border-border px-3 py-2 text-body text-fg-primary hover:bg-state-hover"
            onClick={() => setDialog('refund')}
          >
            Refund order
          </button>
        ) : null}
      </div>

      <h2 className="mt-8 text-h3 font-semibold text-fg-primary">Payment history</h2>
      {/* ClickUp 869f084w5 §1 asked for the user's platform, App / Web / both.
          Measured 16.09.2026 on dev AND prod: every row of payment-back's
          store carries platform = 'WEB' (dev: 570/570 payment methods,
          584/584 subscriptions), `profiles` has no platform column, and there
          is no App client. So the honest thing to print is Web, once, with
          what it rests on — not a per-row column implying a choice that has
          never had a second value. */}
      <p className="mt-1 text-meta text-fg-tertiary">
        Platform: <span className="font-medium text-fg-secondary">Web</span> — every payment
        record on this platform is Web. There is no App client yet, so &quot;App / Web / both&quot;
        has only one possible answer today.
      </p>
      {paymentsQuery.isPending ? (
        <p className="mt-2 text-body text-fg-secondary">Loading…</p>
      ) : paymentsQuery.isError ? (
        <p className="mt-2 text-body text-fg-secondary">Could not load payment history.</p>
      ) : paymentsQuery.data.history.length === 0 ? (
        <p className="mt-2 text-body text-fg-secondary">No payment events recorded for this user.</p>
      ) : (
        <table className="mt-2 w-full text-left text-body text-fg-primary">
          <thead>
            <tr className="border-b border-border text-meta text-fg-secondary">
              <th className="py-2 pr-4 font-medium">Date</th>
              <th className="py-2 pr-4 font-medium">Kind</th>
              <th className="py-2 pr-4 font-medium">Provider</th>
              <th className="py-2 pr-4 font-medium">Reference</th>
            </tr>
          </thead>
          <tbody>
            {paymentsQuery.data.history.map((row) => (
              <tr key={`${row.kind}-${row.provider_ref}-${row.processed_at}`} className="border-b border-border">
                <td className="py-2 pr-4">{new Date(row.processed_at).toLocaleString()}</td>
                <td className="py-2 pr-4">{row.kind}</td>
                <td className="py-2 pr-4">{row.provider}</td>
                <td className="py-2 pr-4">{row.provider_ref}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* ONE implementation of account deletion, shared with the user's card
          (components/DeleteAccountDialog). This page used to carry its own
          inline copy of the preview/reason/confirm-wall; the card now offers
          the same door, and two inline copies of an IRREVERSIBLE flow is how
          one of them quietly ends up with the weaker wall. The entry point
          here is KEPT — it is what the Payments lookup links to, and a
          bookmark that stops working is its own bug report. */}
      {canDelete ? (
        <section className="mt-10 rounded-card border border-destructive-border bg-destructive-soft p-4">
          <h2 className="text-h3 font-semibold text-destructive-soft-foreground">Delete account</h2>
          <p className="mt-2 text-body text-destructive-soft-foreground">
            Irreversible. Preview what will be erased before confirming.
          </p>
          <button
            type="button"
            className="mt-3 rounded-card border border-destructive-border px-3 py-2 text-body font-medium text-destructive-soft-foreground hover:bg-destructive-hover"
            onClick={() => setDeleteOpen(true)}
          >
            Preview deletion
          </button>
        </section>
      ) : null}

      {deleteOpen ? (
        <DeleteAccountDialog
          userId={profileId}
          onClose={() => setDeleteOpen(false)}
          onSuccess={(outcome) => setBanner({ kind: 'success', message: `Account ${outcome}.` })}
        />
      ) : null}

      {dialog === 'cancel' ? (
        <ReasonDialog
          title="Cancel subscription"
          description="Cancels every active subscription of this user's organization at payment-back."
          confirmLabel="Cancel subscription"
          busy={busy}
          onConfirm={runAction}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'delete-token' ? (
        <ReasonDialog
          title="Delete payment token"
          description="Removes this user's stored payment method(s) at payment-back."
          confirmLabel="Delete token"
          danger
          busy={busy}
          onConfirm={runAction}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'refund' ? (
        <div>
          <div className="fixed inset-x-0 top-0 z-40 flex justify-center pt-4">
            <div className="w-full max-w-md rounded-card border border-border bg-background p-3 shadow-lg">
              <label className="text-meta text-fg-secondary" htmlFor="refund-order-id">
                Order id
              </label>
              <input
                id="refund-order-id"
                className="mt-1 w-full rounded-card border border-border bg-surface-field p-2 text-body text-fg-primary"
                value={refundOrderId}
                onChange={(e) => setRefundOrderId(e.target.value)}
              />
            </div>
          </div>
          <ReasonDialog
            title="Refund order"
            description="Refunds a specific order at payment-back."
            confirmLabel="Refund"
            danger
            busy={busy}
            onConfirm={runAction}
            onClose={() => setDialog(null)}
          />
        </div>
      ) : null}
    </div>
  );
}
